/**
 * Copy an answer to the clipboard.
 *
 * The most-used control on this screen, and the reason the product exists in the shape it does: an
 * employee reads an answer and then puts it into an email, a chat with a colleague, or a note on a
 * customer file. Retyping it introduces errors into exactly the material — a fee, a deadline, an
 * account requirement — that the assistant exists to get right.
 *
 * ## What gets copied is not quite what is on screen
 *
 * The answer carries citation markers as `[1]`, `[2]`. Those are rendered as chips, and the chip's
 * DOM text is the bare number — so a naive `innerText` copy yields "the fee is 7 LYD 1" with the
 * brackets gone and the marker reading as part of the sentence. The raw answer text is copied
 * instead, brackets intact, which is also what a colleague receiving it needs in order to ask which
 * document it came from.
 *
 * ## Why it does not silently fail
 *
 * `navigator.clipboard` is unavailable on an insecure origin and can be refused by policy. Both are
 * real on a bank's managed browsers, and both present as nothing happening when the button is
 * pressed. The failure is surfaced, so an employee who cannot copy knows to select the text by hand
 * rather than pasting whatever was in the clipboard before.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { CopyIcon, cx } from "@nab/shared";

import { ACTION_BUTTON } from "./actionButton";

export interface CopyButtonProps {
  /** The answer as the model wrote it, markers and all. */
  text: string;
  className?: string;
}

type State = "idle" | "copied" | "failed";

/** How long the confirmation stays up. Long enough to read, short enough not to become furniture. */
const CONFIRM_MS = 2000;

export function CopyButton({ text, className }: CopyButtonProps): JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<State>("idle");
  const timer = useRef<number | undefined>(undefined);

  // Clearing on unmount matters here: a conversation is a list, and an employee who copies an
  // answer and immediately starts a new conversation unmounts this while the timer is pending.
  useEffect(() => () => window.clearTimeout(timer.current), []);

  async function copy(): Promise<void> {
    window.clearTimeout(timer.current);
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(text);
      setState("copied");
    } catch {
      setState("failed");
    }
    timer.current = window.setTimeout(() => setState("idle"), CONFIRM_MS);
  }

  const label =
    state === "copied"
      ? t("actions.copied")
      : state === "failed"
        ? t("actions.copyFailed")
        : t("actions.copy");

  return (
    <button
      type="button"
      onClick={() => void copy()}
      title={label}
      aria-label={label}
      className={cx(ACTION_BUTTON, state === "failed" && "text-statusDanger", className)}
    >
      <CopyIcon />
      {/* Announced, not just shown: a screen-reader user pressing copy gets no other confirmation
          that anything happened. `role="status"` on an `sr-only` label rather than a separate live
          region, so the button's own name is what changes — and the design's row is icons only. */}
      <span role="status" className="sr-only">
        {label}
      </span>
    </button>
  );
}
